/**
 * useGuardedModeSwitch
 *
 * Shared implementation of "switch designer mode" (draft ⇄ published) used by
 * both `DraftModeContext` and `WorkflowOverflowMenu`. The menu lives in
 * `WorkflowViewTabs` which renders ABOVE `DraftModeProvider`, so it can't
 * consume the context — mirroring `useDraftDiscardActions`, this hook gives
 * both call sites the same dirty-check, confirmation, save-and-switch, and
 * canvas-reload sequence instead of duplicating it inline.
 *
 * Why a guard exists at all:
 *
 *   In draft mode the editable `workflow` in `workflowStore` is ahead of the
 *   `draftWorkflow` snapshot in `draftModeStore` whenever `isDirty` is true
 *   (autosave debounce in flight, or autosave mode set to `off`). Calling
 *   `setWorkflowFiles(prodWorkflow, prodConnections)` to flip into published
 *   view OVERWRITES that live workflow. Switching back to draft then loads
 *   the stale snapshot — the unsaved edits are silently gone. We confirm
 *   before doing that.
 *
 * Direction is asymmetric:
 *
 *   - draft → published needs the guard (above)
 *   - published → draft does not — published is read-only, so the live
 *     workflow is identical to `prodWorkflow` and nothing can be lost
 */

import { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useDraftModeStore } from '@/stores/draftModeStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useUIPreferencesStore } from '@/stores/uiPreferencesStore';
import { useAppStore } from '@/stores/appStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { useAuth } from '@/auth/useAuth';
import { useDraftSave } from '@/hooks/useDraftSave';
import { saveDraftWorkflow } from '@/services/draftWorkflowApi';
import { applySidecarBranch } from '@/services/nodesMetadata';
import { saveNodesMetadata } from '@/services/nodesMetadataApi';
import {
  commitSidecarPlan,
  getCachedSidecar,
  planDraftSidecar,
} from '@/services/nodesMetadataCache';
import { saveMockData } from '@/services/mockDataApi';
import {
  commitMockPlan,
  getCachedMockData,
  planMockData,
} from '@/services/mockDataCache';
import { composeWorkflowKey } from '@/stores/workflowViewPrefsStore';
import { buildDraftSavePayload } from '@/services/workflowSerializer';
import { telemetryService } from '@/services/telemetryService';
import type { WorkflowDefinition } from '@/types/workflowDefinition';

const useStyles = makeStyles({
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    marginTop: tokens.spacingVerticalM,
    display: 'block',
  },
});

export interface UseGuardedModeSwitchOptions {
  /**
   * Workflow name — required for the "Save & switch" path to persist the
   * pending edits before swapping to published view. When omitted, the
   * save button is disabled and only the "Switch without saving" / Cancel
   * actions remain (matching `useDraftDiscardActions`' optional shape).
   */
  workflowName?: string;
}

export interface UseGuardedModeSwitchResult {
  /**
   * Request a switch to published view. If the draft has unsaved changes,
   * opens a confirmation dialog; otherwise switches immediately.
   */
  requestSwitchToPublished: () => void;
  /**
   * Switch back to draft view. No guard — published is read-only so there
   * are no unsaved edits to protect.
   */
  switchToDraft: () => void;
  /**
   * Dialog node ready to mount alongside the trigger (Menu/Toggle). Each
   * caller mounts its own instance — only one is open at a time, so the
   * duplication is structural rather than visual.
   */
  dialog: React.ReactNode;
}

/**
 * Shared mode-switch logic with an unsaved-changes guard for the
 * draft → published direction.
 */
export const useGuardedModeSwitch = ({
  workflowName,
}: UseGuardedModeSwitchOptions = {}): UseGuardedModeSwitchResult => {
  const styles = useStyles();
  const setIsDraftMode = useDraftModeStore((s) => s.setIsDraftMode);
  const setWorkflowFiles = useWorkflowStore((s) => s.setWorkflowFiles);
  const setDraftArtifacts = useDraftModeStore((s) => s.setDraftArtifacts);
  const setDraftStatus = useDraftModeStore((s) => s.setDraftStatus);
  const setStoreLastSavedTime = useDraftModeStore((s) => s.setLastSavedTime);
  const setStoreSaveError = useDraftModeStore((s) => s.setSaveError);
  const markWorkflowClean = useWorkflowStore((s) => s.markClean);
  const markWorkflowDirty = useWorkflowStore((s) => s.markDirty);
  const markConnectionsClean = useConnectionStore((s) => s.markClean);
  const markConnectionsDirty = useConnectionStore((s) => s.markDirty);
  const { getOttoToken } = useAuth();
  const { selectedProject, selectedApp } = useAppStore();

  // The save hook is a no-op when workflowName is missing — instantiate
  // with a sentinel rather than conditionally to keep hook order stable.
  const { save: saveDraft } = useDraftSave({ workflowName: workflowName ?? '' });

  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Re-applies the cached nodesMetadata sidecar for the given mode onto
  // the live workflow tree. Called whenever the canvas swaps its backing
  // workflow object so client-only fields (swaggerPathTemplate, detached,
  // layoutMode, positions) survive the mode toggle. Cache-only — no GET.
  const reapplySidecarForCurrentMode = useCallback(
    (mode: 'draft' | 'published'): void => {
      if (!workflowName || !selectedProject || !selectedApp) return;
      const identity = useWorkflowStore.getState().currentWorkflowIdentity;
      if (!identity) return;
      const sidecar = getCachedSidecar(composeWorkflowKey(identity));
      if (!sidecar) return;
      const live = useWorkflowStore.getState().workflow;
      if (!live) return;
      const { workflow: enriched } = applySidecarBranch(sidecar[mode], live, identity, mode);
      if (enriched && enriched !== live) {
        useWorkflowStore.setState({ workflow: enriched });
      }
    },
    [workflowName, selectedProject, selectedApp],
  );

  // The raw artifact swap. Used by every confirm branch and by the
  // immediate (clean) path.
  const performSwitchToPublished = useCallback(() => {
    const { prodWorkflow, prodConnections, prodNotes } = useDraftModeStore.getState();
    if (prodWorkflow) {
      // Set the target mode BEFORE the swap. `setWorkflowFiles` migrates
      // any legacy embedded positions using `workflowStore.currentMode` —
      // and the `DraftModeContext` effect that mirrors `isDraftMode` into
      // `currentMode` runs *after* React commits, so without this write the
      // migration would land in the draft branch on the way to published
      // (and vice versa for `switchToDraft`).
      useWorkflowStore.setState({ currentMode: 'published' });
      // Notes live in a sidecar file but are part of the workflow schema;
      // merge them in so the canvas Notes panel renders content for the
      // published branch (matches `WorkflowDesignerPage`'s initial load).
      const workflowWithNotes = prodNotes
        ? { ...prodWorkflow, notes: prodNotes as WorkflowDefinition['notes'] }
        : prodWorkflow;
      setWorkflowFiles(workflowWithNotes, prodConnections);
      // Re-apply the published branch of the sidecar so swaggerPathTemplate
      // / detached / layoutMode and the published-mode positions are
      // restored after the swap.
      reapplySidecarForCurrentMode('published');
    }
    // Mocks are draft-only: published view must never show them. `setWorkflowFiles`
    // already resets the slice when prodWorkflow exists; clearing explicitly also
    // covers the no-prod path and documents the invariant. Non-dirtying.
    useWorkflowStore.getState().clearMocks();
    setIsDraftMode(false);
  }, [setIsDraftMode, setWorkflowFiles, reapplySidecarForCurrentMode]);

  const switchToDraft = useCallback(() => {
    const { draftWorkflow, draftConnections, draftNotes } = useDraftModeStore.getState();
    if (draftWorkflow) {
      useWorkflowStore.setState({ currentMode: 'draft' });
      // Prefer draft notes for the draft branch. If the draft notes sidecar is
      // absent, leave the workflow without notes (avoids leaking published notes
      // into draft view).
      const workflowWithNotes = draftNotes
        ? { ...draftWorkflow, notes: draftNotes as WorkflowDefinition['notes'] }
        : draftWorkflow;
      setWorkflowFiles(workflowWithNotes, draftConnections);
      reapplySidecarForCurrentMode('draft');
      // `setWorkflowFiles` cleared the mock slice; re-hydrate the draft's mocks
      // from the cached sidecar (cache-only, no GET). Published has no mocks,
      // so there's no equivalent on `performSwitchToPublished`.
      const identity = useWorkflowStore.getState().currentWorkflowIdentity;
      if (identity) {
        const cached = getCachedMockData(composeWorkflowKey(identity));
        if (cached) {
          useWorkflowStore
            .getState()
            .hydrateMocks(cached.mockedNodes, cached.mockRunEnabled);
        }
      }
    }
    setIsDraftMode(true);
  }, [setIsDraftMode, setWorkflowFiles, reapplySidecarForCurrentMode]);

  /**
   * Switch to published view IMMEDIATELY and fire the draft save in the
   * background using a snapshot of the dirty state.
   *
   * This is the "feels-instant" path used when autosave is enabled. The
   * critical correctness rule is: the save MUST use data captured *before*
   * `performSwitchToPublished` runs. After the switch, `workflowStore.workflow`
   * holds the prod definition — reading it inside an async `mutationFn`
   * (as `useDraftSave` does) would persist prod as the draft and silently
   * destroy the user's edits. So we bypass `useDraftSave` here and call
   * `saveDraftWorkflow` directly with the captured snapshot.
   *
   * We also push the snapshot into `draftModeStore.draftWorkflow/Connections`
   * so toggling back to draft shows the user's edits (not stale prod), and
   * mark both stores clean so the status pill doesn't flash "dirty" forever
   * after the optimistic switch.
   *
   * On failure we surface a notification with a retry that re-fires the
   * same snapshot — not a fresh save, since by then the live store holds
   * prod (or whatever the user has since edited) and re-saving that would
   * compound the data loss.
   */
  const performBackgroundSaveAndSwitch = useCallback((): boolean => {
    if (!workflowName || !selectedProject || !selectedApp) return false;
    const live = useWorkflowStore.getState().workflow;
    if (!live) {
      // Nothing to snapshot — just flip the UI.
      performSwitchToPublished();
      return true;
    }

    // Build the save payload NOW so the background save uses the user's
    // edits, not whatever's in the live store after the switch overwrites
    // it. Snapshot `draftParameters` too so template-mode parameter edits
    // aren't dropped from the background save / retry.
    const liveParameters = useDraftModeStore.getState().draftParameters;
    let payload: ReturnType<typeof buildDraftSavePayload>;
    try {
      payload = buildDraftSavePayload(
        live,
        useConnectionStore.getState().exportToConnectionsConfig(),
        liveParameters,
      );
    } catch (err) {
      telemetryService.trackEvent('workflow.mode.background_save_serialize_failed', {
        error: err instanceof Error ? err.message : 'unknown',
      });
      return false;
    }

    // Lock the snapshot into the draft store BEFORE switching so toggling
    // back to draft loads the user's edits, not stale prod.
    setDraftArtifacts({
      workflow: live,
      connections: payload.connectionsConfig ?? null,
      ...(payload.parametersConfig && { parameters: payload.parametersConfig }),
      ...(payload.notesConfig && { notes: payload.notesConfig }),
    });
    // Snapshot changeCount BEFORE the optimistic markClean. `ownBumps`
    // tracks re-mark-dirty calls in the failure handler so a retry-success
    // can still distinguish "no further user edits" from "user edited".
    const snapshotChangeCounts = {
      workflow: useWorkflowStore.getState().changeCount,
      connections: useConnectionStore.getState().changeCount,
    };
    const ownBumps = { workflow: 0, connections: 0 };
    // Snapshot the draft mock slice BEFORE the switch — `performSwitchToPublished`
    // calls `setWorkflowFiles`/`clearMocks`, which reset `mockedNodes`/`mockRunEnabled`.
    const mockSnapshot = {
      mockedNodes: useWorkflowStore.getState().mockedNodes,
      mockRunEnabled: useWorkflowStore.getState().mockRunEnabled ?? false,
    };
    // Optimistically write the snapshot into the mock cache BEFORE the switch,
    // mirroring the `setDraftArtifacts` lock above. `switchToDraft` re-hydrates
    // mocks from this cache, so committing here (independent of the background
    // save's outcome) means a *failed* save no longer drops the user's unsaved
    // mock edits on the way back to draft. The on-disk PUT still happens in
    // `runSave`; on success it re-commits the identical plan (idempotent).
    const mockPlan = planMockData(
      mockSnapshot.mockedNodes,
      mockSnapshot.mockRunEnabled,
      useWorkflowStore.getState().currentWorkflowIdentity,
    );
    if (mockPlan) commitMockPlan(mockPlan);
    markWorkflowClean();
    markConnectionsClean();
    setDraftStatus('saving');

    performSwitchToPublished();

    // Closure-captured `payload` lets the retry button replay the same
    // snapshot — reading live state would re-save whatever the user has
    // edited since, compounding any data loss.
    const runSave = async (): Promise<void> => {
      const identity = useWorkflowStore.getState().currentWorkflowIdentity;
      // Build the sidecar from the snapshot BEFORE firing the workflow PUT
      // so both writes run in parallel. `mockPlan` was built + cached
      // optimistically above; reuse it here for the on-disk PUT.
      const sidecarPlan = planDraftSidecar(live, identity);
      const ctx = {
        projectName: selectedProject,
        appName: selectedApp.name,
        getToken: getOttoToken,
        workflowName,
      };

      const [result, sidecarOk, mockOk] = await Promise.all([
        saveDraftWorkflow({ ...ctx, ...payload, skipValidation: true }),
        sidecarPlan ? saveNodesMetadata(ctx, sidecarPlan.file) : Promise.resolve(false),
        mockPlan ? saveMockData(ctx, mockPlan.file) : Promise.resolve(false),
      ]);

      if (!result.success) {
        throw new Error(result.error || 'Failed to save draft');
      }
      if (sidecarPlan && sidecarOk) {
        commitSidecarPlan(sidecarPlan);
      }
      if (mockPlan && mockOk) {
        commitMockPlan(mockPlan);
      }

      // Only re-clean if changeCount hasn't advanced beyond
      // snapshot + our own markDirty bumps — otherwise user edits made
      // during the save would be silently marked saved.
      if (
        useWorkflowStore.getState().changeCount ===
        snapshotChangeCounts.workflow + ownBumps.workflow
      ) {
        markWorkflowClean();
      }
      if (
        useConnectionStore.getState().changeCount ===
        snapshotChangeCounts.connections + ownBumps.connections
      ) {
        markConnectionsClean();
      }
      setStoreLastSavedTime(new Date());
      setStoreSaveError(null);
      setDraftStatus('saved');
      telemetryService.trackEvent('workflow.mode.background_save_succeeded');
    };

    const handleSaveFailure = (err: unknown, telemetryEvent: string): void => {
      const message = err instanceof Error ? err.message : 'Failed to save draft';
      setStoreSaveError(message);
      setDraftStatus('error');
      // Re-dirty so the status pill, manual save, and autosave loop all
      // reflect the unsaved state when the user returns to draft.
      markWorkflowDirty();
      ownBumps.workflow += 1;
      markConnectionsDirty();
      ownBumps.connections += 1;
      telemetryService.trackEvent(telemetryEvent, { error: message });
    };

    runSave().catch((err: unknown) => {
      handleSaveFailure(err, 'workflow.mode.background_save_failed');

      // Published view may not show the draft status pill — surface a
      // recoverable notification so the user knows their edits weren't
      // persisted and can retry.
      useNotificationStore.getState().addNotification({
        type: 'error',
        title: 'Draft save failed',
        message:
          "Your switch to the published view succeeded, but your draft edits couldn't be saved to the backend. Retry to try again.",
        actionButton: {
          label: 'Retry save',
          onClick: () => {
            setDraftStatus('saving');
            runSave().catch((retryErr: unknown) =>
              handleSaveFailure(retryErr, 'workflow.mode.background_save_retry_failed'),
            );
          },
        },
      });
    });

    return true;
  }, [
    workflowName,
    selectedProject,
    selectedApp,
    getOttoToken,
    setDraftArtifacts,
    markWorkflowClean,
    markWorkflowDirty,
    markConnectionsClean,
    markConnectionsDirty,
    setDraftStatus,
    setStoreLastSavedTime,
    setStoreSaveError,
    performSwitchToPublished,
  ]);

  const requestSwitchToPublished = useCallback(() => {
    const workflowDirty = useWorkflowStore.getState().isDirty;
    const connectionsDirty = useConnectionStore.getState().isDirty;
    const hasUnsaved = workflowDirty || connectionsDirty;

    if (!hasUnsaved) {
      telemetryService.trackEvent('workflow.mode.switch_to_published', {
        hasUnsaved: 'false',
      });
      performSwitchToPublished();
      return;
    }

    // Autosave-on users have already opted into having pending edits
    // flushed automatically — the dialog's "Save & switch" is just doing
    // what autosave would have done in the next debounce window. Flip the
    // UI optimistically and let the save complete in the background. The
    // snapshot inside `performBackgroundSaveAndSwitch` preserves the edits
    // even if the save fails (we surface a retry notification then).
    const autosaveOn = useUIPreferencesStore.getState().draftAutosaveMode !== 'off';
    if (autosaveOn && workflowName) {
      telemetryService.trackEvent('workflow.mode.unsaved_switch_autosaved');
      const started = performBackgroundSaveAndSwitch();
      if (started) return;
      // If the background path bailed (e.g. serialization failed), fall
      // through to the dialog so the user can decide explicitly.
    }

    telemetryService.trackEvent('workflow.mode.unsaved_switch_prompted');
    setSaveError(null);
    setIsOpen(true);
  }, [performSwitchToPublished, performBackgroundSaveAndSwitch, workflowName]);

  const closeDialog = useCallback(() => {
    // Don't allow dismiss while a save is in flight — the post-save switch
    // is still pending and closing here would leave the user in an
    // ambiguous state.
    if (isSaving) return;
    setIsOpen(false);
    setSaveError(null);
  }, [isSaving]);

  const handleCancel = useCallback(() => {
    if (isSaving) return;
    telemetryService.trackEvent('workflow.mode.unsaved_switch_cancelled');
    setIsOpen(false);
    setSaveError(null);
  }, [isSaving]);

  const handleSaveAndSwitch = useCallback(async () => {
    if (!workflowName) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      // Dialog path is the await-then-switch flow: save completes BEFORE
      // the switch overwrites the live store, so reading live state inside
      // `useDraftSave`'s mutationFn is safe. This is intentionally
      // different from the autosave-on background path — see
      // `performBackgroundSaveAndSwitch` for the snapshot-based variant.
      const result = await saveDraft();
      if (!result.success) {
        // Keep the dialog open so the user can either retry, discard,
        // or cancel. Surfacing the message inline avoids the silent
        // "I clicked Save & switch and nothing happened" failure mode.
        setSaveError(result.error ?? 'Failed to save draft.');
        telemetryService.trackEvent('workflow.mode.unsaved_switch_save_failed', {
          error: result.error ?? 'unknown',
          path: 'dialog',
        });
        return;
      }
      telemetryService.trackEvent('workflow.mode.unsaved_switch_saved');
      setIsOpen(false);
      performSwitchToPublished();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save draft.';
      setSaveError(message);
      telemetryService.trackEvent('workflow.mode.unsaved_switch_save_failed', {
        error: message,
        path: 'dialog',
      });
    } finally {
      setIsSaving(false);
    }
  }, [workflowName, saveDraft, performSwitchToPublished]);

  const handleSwitchWithoutSaving = useCallback(() => {
    if (isSaving) return;
    telemetryService.trackEvent('workflow.mode.unsaved_switch_discarded');
    setIsOpen(false);
    setSaveError(null);
    performSwitchToPublished();
  }, [isSaving, performSwitchToPublished]);

  const dialog = useMemo(
    () => (
      <Dialog
        open={isOpen}
        onOpenChange={(_, data) => {
          if (!data.open) closeDialog();
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Unsaved changes</DialogTitle>
            <DialogContent>
              You have unsaved draft changes. Switching to the published view
              now will discard them. Save first to keep your edits, or switch
              without saving to discard them.
              {saveError && (
                <Text className={styles.errorText} role="alert">
                  {saveError}
                </Text>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                size="small"
                appearance="secondary"
                onClick={handleCancel}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                size="small"
                appearance="secondary"
                onClick={handleSwitchWithoutSaving}
                disabled={isSaving}
              >
                Switch without saving
              </Button>
              <Button
                size="small"
                appearance="primary"
                onClick={() => { void handleSaveAndSwitch(); }}
                disabled={isSaving || !workflowName}
                {...(isSaving ? { icon: <Spinner size="tiny" /> } : {})}
              >
                {isSaving ? 'Saving…' : 'Save & switch'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    ),
    [
      isOpen,
      isSaving,
      saveError,
      workflowName,
      closeDialog,
      handleCancel,
      handleSwitchWithoutSaving,
      handleSaveAndSwitch,
      styles.errorText,
    ],
  );

  return {
    requestSwitchToPublished,
    switchToDraft,
    dialog,
  };
};
